import React, { useState, useEffect, useCallback, useRef } from 'react';
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import Typography from '@material-ui/core/Typography';
import Chip from '@material-ui/core/Chip';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import CircularProgress from '@material-ui/core/CircularProgress';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import VisibilityIcon from '@material-ui/icons/Visibility';
import LinkIcon from '@material-ui/icons/Link';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import BlockIcon from '@material-ui/icons/Block';
import UndoIcon from '@material-ui/icons/Undo';
import { makeStyles } from '@material-ui/core/styles';
import PagePreview from './PagePreview';

const MAPPING_STATUS_COLORS = {
  mapped: '#4caf50',
  suggested: '#2196f3',
  unmapped: '#9e9e9e',
  ignored: '#757575',
};

const getScoreColor = (score) => {
  if (score >= 0.85) return '#4caf50';
  if (score >= 0.50) return '#ff9800';
  return '#f44336';
};

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: 450,
    flexShrink: 0,
  },
  drawerPaper: {
    width: 450,
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(0.5),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  fieldLabel: {
    fontSize: '0.7rem',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    letterSpacing: '0.5px',
  },
  fieldValue: {
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
  },
  chip: {
    color: '#fff',
    fontWeight: 500,
    marginLeft: theme.spacing(1),
  },
  rawTextToggle: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    marginBottom: theme.spacing(1),
  },
  rawText: {
    fontFamily: 'monospace',
    fontSize: '0.8rem',
    backgroundColor: '#f5f5f5',
    padding: theme.spacing(2),
    borderRadius: 4,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: 300,
    overflow: 'auto',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  viewPageBtn: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  mappingDivider: {
    margin: theme.spacing(2, 0),
  },
  suggestionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  suggestionInfo: {
    flex: 1,
    minWidth: 0,
    marginRight: theme.spacing(1),
  },
  suggestionName: {
    fontSize: '0.9rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  suggestionUnit: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  scoreBadge: {
    color: '#fff',
    fontWeight: 600,
    fontSize: '0.7rem',
    marginRight: theme.spacing(1),
    minWidth: 44,
  },
  mappedInfo: {
    backgroundColor: '#f1f8e9',
    borderRadius: 4,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  mappedItemName: {
    fontWeight: 600,
    fontSize: '0.95rem',
  },
  mappedDetail: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  ignoredSection: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  searchField: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  ignoreBtn: {
    marginTop: theme.spacing(1.5),
  },
}));

const Field = ({ label, value, classes }) => (
  <div className={classes.field}>
    <Typography className={classes.fieldLabel}>{label}</Typography>
    <Typography className={classes.fieldValue}>{value || '-'}</Typography>
  </div>
);

const VendorProductDetail = ({ productId, open, onClose }) => {
  const classes = useStyles();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rawTextOpen, setRawTextOpen] = useState(false);
  const [pagePreviewOpen, setPagePreviewOpen] = useState(false);

  // Mapping state
  const [mapping, setMapping] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const searchDebounceRef = useRef(null);

  const fetchProduct = useCallback(() => {
    if (!productId) return;
    setLoading(true);
    setProduct(null);
    fetch(`/api/vendor-catalogs/products/${productId}`)
      .then((res) => res.json())
      .then((data) => {
        setProduct(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [productId]);

  const fetchMapping = useCallback(() => {
    if (!productId) return;
    fetch(`/api/vendor-catalogs/products/${productId}/mapping`)
      .then((res) => {
        if (res.ok) return res.json();
        return null;
      })
      .then((data) => setMapping(data))
      .catch(() => setMapping(null));
  }, [productId]);

  const fetchSuggestions = useCallback(() => {
    if (!productId) return;
    fetch(`/api/vendor-catalogs/products/${productId}/suggestions`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setSuggestions(data);
        else setSuggestions([]);
      })
      .catch(() => setSuggestions([]));
  }, [productId]);

  // Fetch product on open
  useEffect(() => {
    if (open && productId) {
      fetchProduct();
    }
  }, [open, productId, fetchProduct]);

  // Fetch mapping + suggestions when product loads
  useEffect(() => {
    if (product && productId) {
      fetchMapping();
      if (product.mappingStatusSummary === 'unmapped') {
        fetchSuggestions();
      }
    }
  }, [product, productId, fetchMapping, fetchSuggestions]);

  // Debounced catalog search
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    if (!searchInput || searchInput.length < 2) {
      setSearchResults([]);
      return;
    }
    searchDebounceRef.current = setTimeout(() => {
      fetch(`/api/bid-estimation/catalog?search=${encodeURIComponent(searchInput)}&limit=10`)
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) setSearchResults(data);
          else setSearchResults([]);
        })
        .catch(() => setSearchResults([]));
    }, 350);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleMap = async (aspireItemId, matchedBy, confidenceScore) => {
    setMappingLoading(true);
    try {
      const res = await fetch(`/api/vendor-catalogs/products/${productId}/map`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aspireItemId, matchedBy, confidenceScore }),
      });
      if (!res.ok) throw new Error('Map failed');
      showSnackbar('Product mapped successfully');
      fetchProduct();
      fetchMapping();
    } catch {
      showSnackbar('Failed to map product', 'error');
    } finally {
      setMappingLoading(false);
    }
  };

  const handleIgnore = async () => {
    setMappingLoading(true);
    try {
      const res = await fetch(`/api/vendor-catalogs/products/${productId}/ignore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Ignore failed');
      showSnackbar('Product ignored');
      fetchProduct();
      fetchMapping();
    } catch {
      showSnackbar('Failed to ignore product', 'error');
    } finally {
      setMappingLoading(false);
    }
  };

  const handleUnmap = async () => {
    setMappingLoading(true);
    try {
      const res = await fetch(`/api/vendor-catalogs/products/${productId}/unmap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Unmap failed');
      showSnackbar('Mapping removed');
      setMapping(null);
      setSuggestions([]);
      fetchProduct();
    } catch {
      showSnackbar('Failed to unmap product', 'error');
    } finally {
      setMappingLoading(false);
    }
  };

  const mappingStatus = product?.mappingStatusSummary || 'unmapped';
  const mappingColor = MAPPING_STATUS_COLORS[mappingStatus] || '#9e9e9e';
  const catalog = product?.vendorCatalogId;

  const renderMappingSection = () => {
    if (!product) return null;

    if (mappingLoading) {
      return (
        <div className={classes.section}>
          <Divider className={classes.mappingDivider} />
          <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
            <CircularProgress size={24} />
          </div>
        </div>
      );
    }

    // MAPPED state
    if (mappingStatus === 'mapped') {
      const aspireItem = mapping?.aspireItemId;
      return (
        <div className={classes.section}>
          <Divider className={classes.mappingDivider} />
          <Typography className={classes.sectionTitle}>Mapped To</Typography>
          <div className={classes.mappedInfo}>
            <Typography className={classes.mappedItemName}>
              {aspireItem?.ItemName || 'Aspire Item'}
            </Typography>
            {aspireItem?.UnitTypeName && (
              <Typography className={classes.mappedDetail}>
                Unit: {aspireItem.UnitTypeName}
              </Typography>
            )}
            {aspireItem?.CategoryName && (
              <Typography className={classes.mappedDetail}>
                Category: {aspireItem.CategoryName}
              </Typography>
            )}
          </div>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LinkOffIcon />}
            onClick={handleUnmap}
          >
            Unmap
          </Button>
        </div>
      );
    }

    // IGNORED state
    if (mappingStatus === 'ignored') {
      return (
        <div className={classes.section}>
          <Divider className={classes.mappingDivider} />
          <div className={classes.ignoredSection}>
            <Chip label="Ignored" size="small" style={{ backgroundColor: '#757575', color: '#fff' }} />
            <Button
              variant="outlined"
              size="small"
              startIcon={<UndoIcon />}
              onClick={handleUnmap}
            >
              Undo Ignore
            </Button>
          </div>
        </div>
      );
    }

    // UNMAPPED state — show suggestions + search
    return (
      <div className={classes.section}>
        <Divider className={classes.mappingDivider} />
        <Typography className={classes.sectionTitle}>Map to Internal Catalog</Typography>

        {/* Suggestions */}
        {suggestions.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {suggestions.slice(0, 3).map((s) => (
              <div key={s.aspireItemId} className={classes.suggestionItem}>
                <div className={classes.suggestionInfo}>
                  <Typography className={classes.suggestionName} title={s.itemName}>
                    {s.itemName}
                  </Typography>
                  <Typography className={classes.suggestionUnit}>
                    {s.unitTypeName || '-'}{s.matchType ? ` | ${s.matchType}` : ''}
                  </Typography>
                </div>
                <Chip
                  label={`${Math.round(s.score * 100)}%`}
                  size="small"
                  className={classes.scoreBadge}
                  style={{ backgroundColor: getScoreColor(s.score) }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={() => handleMap(s.aspireItemId, s.matchType, s.score)}
                  disabled={mappingLoading}
                >
                  Map
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Manual Search */}
        <Autocomplete
          className={classes.searchField}
          options={searchResults}
          getOptionLabel={(option) =>
            option.itemName
              ? `${option.itemName} (${option.unitTypeName || '-'})`
              : ''
          }
          renderOption={(option) => (
            <div>
              <Typography style={{ fontSize: '0.9rem', fontWeight: 500 }}>
                {option.itemName}
                {option.itemCode ? <span style={{ color: '#888', fontWeight: 400, marginLeft: 6, fontSize: '0.8rem' }}>{option.itemCode}</span> : null}
              </Typography>
              <Typography style={{ fontSize: '0.75rem', color: '#888' }}>
                {option.unitTypeName || '-'} | {option.categoryName || '-'}
              </Typography>
            </div>
          )}
          onInputChange={(_, value) => setSearchInput(value)}
          onChange={(_, selected) => {
            if (selected && selected.aspireItemId) {
              handleMap(selected.aspireItemId, 'manual');
            }
          }}
          loading={searchInput.length >= 2 && searchResults.length === 0}
          noOptionsText={searchInput.length < 2 ? 'Type to search...' : 'No items found'}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search Aspire Catalog"
              variant="outlined"
              size="small"
              placeholder="Type item name or code..."
            />
          )}
        />

        {/* Ignore Button */}
        <Button
          className={classes.ignoreBtn}
          variant="outlined"
          size="small"
          color="default"
          startIcon={<BlockIcon />}
          onClick={handleIgnore}
          disabled={mappingLoading}
        >
          Ignore This Product
        </Button>
      </div>
    );
  };

  return (
    <>
      <Drawer
        className={classes.drawer}
        anchor="right"
        open={open}
        onClose={onClose}
        classes={{ paper: classes.drawerPaper }}
      >
        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : product ? (
          <>
            <div className={classes.header}>
              <div>
                <Typography variant="h6">{product.productName || 'Untitled Product'}</Typography>
                <Chip
                  label={mappingStatus}
                  size="small"
                  className={classes.chip}
                  style={{ backgroundColor: mappingColor, marginLeft: 0, marginTop: 8 }}
                />
              </div>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </div>

            {/* Identity Section */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>Identity</Typography>
              <Field label="Vendor Item Number" value={product.vendorItemNumber} classes={classes} />
              <Field label="Description" value={product.description} classes={classes} />
              <Field label="Manufacturer" value={product.manufacturer} classes={classes} />
              <Field label="Part Number" value={product.partNumber} classes={classes} />
              <Field label="Category" value={product.category} classes={classes} />
              <Field label="Subcategory" value={product.subcategory} classes={classes} />
            </div>

            {/* Source Section */}
            <div className={classes.section}>
              <Typography className={classes.sectionTitle}>Source</Typography>
              <Field
                label="Catalog Name"
                value={catalog?.catalogName}
                classes={classes}
              />
              <Field label="Version" value={catalog?.version} classes={classes} />
              <Field label="Page" value={product.pageNumber} classes={classes} />
              <Field
                label="Confidence"
                value={
                  product.confidence != null
                    ? `${Math.round(product.confidence * 100)}%`
                    : '-'
                }
                classes={classes}
              />
              <Field label="Extraction Status" value={product.extractionStatus} classes={classes} />
            </div>

            {/* View Catalog Page Button */}
            {catalog?._id && product.pageNumber != null && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<VisibilityIcon />}
                className={classes.viewPageBtn}
                onClick={() => setPagePreviewOpen(true)}
              >
                View Catalog Page
              </Button>
            )}

            {/* Mapping Section */}
            {renderMappingSection()}

            {/* Raw Extracted Text */}
            {product.rawExtractedText && (
              <div className={classes.section}>
                <div
                  className={classes.rawTextToggle}
                  onClick={() => setRawTextOpen(!rawTextOpen)}
                >
                  {rawTextOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  <Typography variant="body2" style={{ marginLeft: 4 }}>
                    Raw Extracted Text
                  </Typography>
                </div>
                <Collapse in={rawTextOpen}>
                  <div className={classes.rawText}>{product.rawExtractedText}</div>
                </Collapse>
              </div>
            )}
          </>
        ) : (
          <Typography color="textSecondary">No product data available.</Typography>
        )}
      </Drawer>

      <PagePreview
        open={pagePreviewOpen}
        onClose={() => setPagePreviewOpen(false)}
        catalogId={catalog?._id}
        pageNum={product?.pageNumber}
        catalogName={catalog?.catalogName}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VendorProductDetail;
